// HEADER-DOCS-012: DocumentsTab
// Spec: §3.3, §3.5, §4.3, §5.2, §19.C
//
// Hero row (latest transcript + latest resume) + filters + document grid + Load More.
// Upload / Add link visible to counselor + student. Parent is read-only.
// Mounted from counselor, student and parent views (HEADER-DOCS-013/014) via `viewerRole`.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import LinkIcon from '@mui/icons-material/Link';
import DescriptionIcon from '@mui/icons-material/Description';

import {
  documentsApi,
  DocumentDto,
  DocType,
  DocOrigin,
  DOC_TYPES,
  DOC_TYPE_LABELS,
} from '../../../../services/camMirrorService';
import { getUserId } from '../../../../utils/sessionStorage';
import { DocumentCard } from './DocumentCard';
import { UploadFileModal } from './UploadFileModal';
import { AddLinkModal } from './AddLinkModal';
import { EditDocumentModal } from './EditDocumentModal';
import { ViewerRole } from './documentPermissions';
import SelectInput from 'components/input/SelectInput';

interface DocumentsTabProps {
  studentId: number;
  viewerRole: ViewerRole;
  studentFirstName?: string;
  /** Logged-in user id (for the student "edit own" check). Defaults to the session user id. */
  currentUserId?: number | null;
}

const PAGE_SIZE = 50;

type HeroType = 'transcript' | 'resume';

export const DocumentsTab: React.FC<DocumentsTabProps> = ({
  studentId,
  viewerRole,
  studentFirstName = 'the student',
  currentUserId,
}) => {
  const [docs, setDocs] = useState<DocumentDto[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [filterDocType, setFilterDocType] = useState<DocType | 'all'>('all');
  const [filterOrigin, setFilterOrigin] = useState<DocOrigin | 'all'>('all');
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hero, setHero] = useState<Record<HeroType, DocumentDto | null | undefined>>({
    transcript: undefined,
    resume: undefined,
  });
  // Modal state; defaultDocType pre-selects the type when opened from a hero slot.
  const [uploadOpen, setUploadOpen] = useState<{ defaultDocType?: DocType } | null>(null);
  const [addLinkOpen, setAddLinkOpen] = useState<{ defaultDocType?: DocType } | null>(null);
  const [editingDoc, setEditingDoc] = useState<DocumentDto | null>(null);

  // Ignore responses from superseded requests (fast filter switching).
  const requestSeq = useRef(0);

  const selfId = currentUserId !== undefined ? currentUserId : parseUserId(getUserId());
  const canWrite = viewerRole === 'counselor' || viewerRole === 'student';

  const listParams = useCallback(
    (cursor?: string) => ({
      studentId,
      docType: filterDocType === 'all' ? undefined : filterDocType,
      origin: filterOrigin === 'all' ? undefined : filterOrigin,
      limit: PAGE_SIZE,
      cursor,
    }),
    [studentId, filterDocType, filterOrigin]
  );

  // ---------------------------------------------------------------------------
  // Load grid (first page) — re-runs when filters change
  // ---------------------------------------------------------------------------
  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoadErr(null);
    setDocs(null);
    try {
      const result = await documentsApi.list(listParams());
      if (seq !== requestSeq.current) return;
      setDocs(result.items);
      setNextCursor(result.nextCursor);
      setHasMore(result.hasMore);
    } catch {
      if (seq !== requestSeq.current) return;
      setDocs([]);
      setHasMore(false);
      setLoadErr("We couldn't load documents. Please refresh.");
    }
  }, [listParams]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    const seq = requestSeq.current;
    setLoadingMore(true);
    try {
      const result = await documentsApi.list(listParams(nextCursor));
      if (seq !== requestSeq.current) return;
      setDocs((prev) => [...(prev ?? []), ...result.items]);
      setNextCursor(result.nextCursor);
      setHasMore(result.hasMore);
    } catch {
      setLoadErr("We couldn't load more. Please try again.");
    } finally {
      setLoadingMore(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Hero slots per spec §3.5 — loaded independently of the grid filters, so a
  // filter like "Essay draft" never makes the hero falsely say "No transcript yet".
  // ---------------------------------------------------------------------------
  const loadHero = useCallback(async () => {
    const fetchLatest = async (docType: HeroType) => {
      try {
        const r = await documentsApi.list({ studentId, docType, limit: 1 });
        return r.items[0] ?? null;
      } catch {
        return null;
      }
    };
    const [transcript, resume] = await Promise.all([
      fetchLatest('transcript'),
      fetchLatest('resume'),
    ]);
    setHero({ transcript, resume });
  }, [studentId]);

  useEffect(() => {
    loadHero();
  }, [loadHero]);

  // ---------------------------------------------------------------------------
  // Local list updates after mutations
  // ---------------------------------------------------------------------------
  const matchesFilters = (d: DocumentDto) =>
    (filterDocType === 'all' || d.docType === filterDocType) &&
    (filterOrigin === 'all' || d.origin === filterOrigin);

  const onDocCreated = (newDoc: DocumentDto) => {
    if (matchesFilters(newDoc)) setDocs((prev) => [newDoc, ...(prev ?? [])]);
    loadHero();
  };

  const onDocUpdated = (updated: DocumentDto) => {
    setDocs((prev) =>
      (prev ?? [])
        .map((d) => (d.externalId === updated.externalId ? updated : d))
        .filter(matchesFilters)
    );
    loadHero();
  };

  const onDocDeleted = (externalId: string) => {
    setDocs((prev) => (prev ?? []).filter((d) => d.externalId !== externalId));
    loadHero();
  };

  const filtersActive = filterDocType !== 'all' || filterOrigin !== 'all';
  const heading =
    viewerRole === 'student' || !studentFirstName
      ? 'Documents'
      : `${studentFirstName}'s documents`;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <Box sx={pageSx}>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'stretch', sm: 'flex-end' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h1" sx={headingSx}>
            {heading}
          </Typography>
          <Typography sx={ledeSx}>
            {viewerRole === 'parent'
              ? 'Files shared with you.'
              : 'Transcripts, resumes, and links in one place.'}
          </Typography>
        </Box>
        {canWrite && (
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <Button startIcon={<CloudUploadOutlinedIcon />} onClick={() => setUploadOpen({})} sx={ghostBtnSx}>
              Upload file
            </Button>
            <Button startIcon={<LinkIcon />} onClick={() => setAddLinkOpen({})} sx={primaryBtnSx}>
              Add link
            </Button>
          </Stack>
        )}
      </Box>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <HeroSlot
          label="Latest transcript"
          doc={hero.transcript}
          emptyText={canWrite ? 'No transcript yet. Upload or link one.' : 'No transcript shared yet.'}
          canWrite={canWrite}
          onUpload={() => setUploadOpen({ defaultDocType: 'transcript' })}
          onAddLink={() => setAddLinkOpen({ defaultDocType: 'transcript' })}
          onOpen={(d) => setEditingDoc(d)}
        />
        <HeroSlot
          label="Latest resume"
          doc={hero.resume}
          emptyText={
            canWrite
              ? `Add ${studentFirstName}'s resume so it's ready when they need it.`
              : 'No resume shared yet.'
          }
          canWrite={canWrite}
          onUpload={() => setUploadOpen({ defaultDocType: 'resume' })}
          onAddLink={() => setAddLinkOpen({ defaultDocType: 'resume' })}
          onOpen={(d) => setEditingDoc(d)}
        />
      </Stack>

      <Box sx={filterBarSx}>
        <Box sx={{ width: { xs: '100%', sm: 220 }, '& > *': { width: '100%' } }}>
          <SelectInput
            id="docs-filter-type"
            label="Type"
            values={filterDocType}
            option={[
              { value: 'all', name: 'All types' },
              ...DOC_TYPES.map((t) => ({ value: t, name: DOC_TYPE_LABELS[t] })),
            ]}
            onChange={(e) => setFilterDocType(e.target.value as DocType | 'all')}
          />
        </Box>
        <Box sx={{ width: { xs: '100%', sm: 220 }, '& > *': { width: '100%' } }}>
          <SelectInput
            id="docs-filter-origin"
            label="Source"
            values={filterOrigin}
            option={[
              { value: 'all', name: 'All sources' },
              { value: 'direct', name: 'Uploaded' },
              { value: 'link', name: 'Linked' },
              { value: 'chat', name: 'From chat' },
            ]}
            onChange={(e) => setFilterOrigin(e.target.value as DocOrigin | 'all')}
          />
        </Box>
      </Box>

      {loadErr && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={load}>Retry</Button>}>
          {loadErr}
        </Alert>
      )}
      {docs == null ? (
        <Box sx={gridSx}>
          {[0, 1, 2].map((i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              sx={{ borderRadius: 'var(--gm-radius-surface, 16px)', bgcolor: 'var(--gm-sunken, #e8f1f7)' }}
              height={168}
            />
          ))}
        </Box>
      ) : docs.length === 0 ? (
        !loadErr && (
          <Box sx={emptySx}>
            <Box aria-hidden sx={emptyIconSx}>
              <DescriptionIcon />
            </Box>
            <Typography sx={{ m: 0, fontWeight: 600, color: 'var(--gm-ink, #1c3350)', fontSize: 'var(--gm-text-lg, 16px)' }}>
              {filtersActive ? 'No documents match these filters.' : 'No documents yet'}
            </Typography>
            <Typography sx={{ m: 0, color: 'var(--gm-ink-muted, #596b8b)', fontSize: 'var(--gm-text-md, 14px)', maxWidth: 420 }}>
              {filtersActive
                ? 'Try another type or source.'
                : viewerRole === 'parent'
                ? 'Nothing has been shared with you yet.'
                : 'Upload a file or add a link to get started.'}
            </Typography>
            {canWrite && !filtersActive && (
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button startIcon={<CloudUploadOutlinedIcon />} onClick={() => setUploadOpen({})} sx={ghostBtnSx}>
                  Upload file
                </Button>
                <Button startIcon={<LinkIcon />} onClick={() => setAddLinkOpen({})} sx={primaryBtnSx}>
                  Add link
                </Button>
              </Stack>
            )}
          </Box>
        )
      ) : (
        <>
          <Box sx={gridSx}>
            {docs.map((d) => (
              <DocumentCard
                key={d.externalId}
                doc={d}
                viewerRole={viewerRole}
                currentUserId={selfId}
                onEdit={() => setEditingDoc(d)}
                onDeleted={() => onDocDeleted(d.externalId)}
              />
            ))}
          </Box>
          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button onClick={loadMore} disabled={loadingMore} sx={ghostBtnSx}>
                {loadingMore ? <CircularProgress size={16} /> : 'Load more'}
              </Button>
            </Box>
          )}
        </>
      )}

      {/* Modals */}
      {uploadOpen && (
        <UploadFileModal
          studentId={studentId}
          defaultDocType={uploadOpen.defaultDocType}
          onClose={() => setUploadOpen(null)}
          onComplete={(d) => {
            onDocCreated(d);
            setUploadOpen(null);
          }}
        />
      )}
      {addLinkOpen && (
        <AddLinkModal
          studentId={studentId}
          defaultDocType={addLinkOpen.defaultDocType}
          onClose={() => setAddLinkOpen(null)}
          onAdded={(d) => {
            onDocCreated(d);
            setAddLinkOpen(null);
          }}
        />
      )}
      {editingDoc && (
        <EditDocumentModal
          doc={editingDoc}
          viewerRole={viewerRole}
          currentUserId={selfId}
          onClose={() => setEditingDoc(null)}
          onUpdated={(d) => {
            onDocUpdated(d);
            setEditingDoc(null);
          }}
          onDeleted={() => {
            onDocDeleted(editingDoc.externalId);
            setEditingDoc(null);
          }}
        />
      )}
    </Box>
  );
};

// -----------------------------------------------------------------------------
// HeroSlot
// -----------------------------------------------------------------------------

interface HeroSlotProps {
  label: string;
  doc: DocumentDto | null | undefined; // undefined = still loading
  emptyText: string;
  canWrite: boolean;
  onUpload: () => void;
  onAddLink: () => void;
  onOpen: (d: DocumentDto) => void;
}

const HeroSlot: React.FC<HeroSlotProps> = ({
  label,
  doc,
  emptyText,
  canWrite,
  onUpload,
  onAddLink,
  onOpen,
}) => (
  <Box
    role={doc ? 'button' : undefined}
    tabIndex={doc ? 0 : undefined}
    onClick={() => doc && onOpen(doc)}
    onKeyDown={(e) => {
      if (doc && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        onOpen(doc);
      }
    }}
    sx={{
      flex: 1,
      minWidth: 0,
      p: 2,
      borderRadius: 'var(--gm-radius-surface, 16px)',
      border: '1px solid var(--gm-hairline, #dde7f0)',
      background: 'var(--gm-surface, #fff)',
      minHeight: 132,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      cursor: doc ? 'pointer' : 'default',
      textAlign: 'left',
      transition: 'border-color 160ms ease',
      '&:hover': doc ? { borderColor: 'var(--gm-accent, #3e73f0)' } : undefined,
      '&:focus-visible': { outline: 'none', boxShadow: '0 0 0 3px var(--gm-accent-tint, #d6e4fd)' },
    }}
  >
    <Typography sx={kickerSx}>{label}</Typography>
    {doc === undefined ? (
      <Skeleton width="60%" sx={{ mt: 1.5, bgcolor: 'var(--gm-sunken, #e8f1f7)' }} />
    ) : doc ? (
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 1.5, minWidth: 0 }}>
        <Box aria-hidden sx={heroIconSx}>
          <DescriptionIcon fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              m: 0,
              fontWeight: 600,
              color: 'var(--gm-ink, #1c3350)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={doc.displayName}
          >
            {doc.displayName}
          </Typography>
          <Typography sx={{ m: 0, mt: 0.25, fontSize: 'var(--gm-text-sm, 12.5px)', color: 'var(--gm-ink-muted, #596b8b)' }}>
            {new Date(doc.createdAt).toLocaleDateString()}
          </Typography>
        </Box>
      </Stack>
    ) : (
      <>
        <Typography sx={{ mt: 1, mb: 0, color: 'var(--gm-ink-body, #475a7e)', fontSize: 'var(--gm-text-md, 14px)' }}>
          {emptyText}
        </Typography>
        {canWrite && (
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <Button
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onUpload();
              }}
              sx={ghostBtnSx}
            >
              Upload
            </Button>
            <Button
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onAddLink();
              }}
              sx={ghostBtnSx}
            >
              Add link
            </Button>
          </Stack>
        )}
      </>
    )}
  </Box>
);

const pageSx = {
  p: { xs: 2, md: 3 },
  background: 'var(--gm-canvas, #f4f9fc)',
  color: 'var(--gm-ink, #1c3350)',
  fontFamily: 'var(--gm-font-ui, Poppins, system-ui, sans-serif)',
  minHeight: '100%',
  '@media (prefers-reduced-motion: reduce)': {
    '& *': { transition: 'none !important' },
  },
};

const headingSx = {
  m: 0,
  fontFamily: 'inherit',
  fontSize: 'var(--gm-text-2xl, 26px)',
  fontWeight: 700,
  letterSpacing: '-0.02em',
  lineHeight: 1.2,
  color: 'var(--gm-ink, #1c3350)',
};

const ledeSx = {
  m: 0,
  mt: 0.75,
  maxWidth: 520,
  color: 'var(--gm-ink-muted, #596b8b)',
  fontSize: 'var(--gm-text-md, 14px)',
  lineHeight: 1.45,
};

const filterBarSx = {
  display: 'flex',
  alignItems: 'flex-end',
  flexWrap: 'wrap',
  gap: 2,
  mb: 3,
  p: 2,
  borderRadius: 'var(--gm-radius-surface, 16px)',
  background: 'var(--gm-surface, #fff)',
  border: '1px solid var(--gm-hairline, #dde7f0)',
};

const gridSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
  gap: 2,
};

const emptySx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: 1,
  py: 6,
  px: 2,
  borderRadius: 'var(--gm-radius-surface, 16px)',
  background: 'var(--gm-surface, #fff)',
  border: '1px solid var(--gm-hairline, #dde7f0)',
};

const emptyIconSx = {
  width: 48,
  height: 48,
  borderRadius: 'var(--gm-radius-nested, 10px)',
  display: 'grid',
  placeItems: 'center',
  mb: 0.5,
  background: 'var(--gm-accent-wash, #edf3fe)',
  color: 'var(--gm-accent-ink, #3568de)',
};

const heroIconSx = {
  width: 40,
  height: 40,
  flexShrink: 0,
  borderRadius: 'var(--gm-radius-nested, 10px)',
  display: 'grid',
  placeItems: 'center',
  background: 'var(--gm-accent-wash, #edf3fe)',
  color: 'var(--gm-accent-ink, #3568de)',
};

const kickerSx = {
  m: 0,
  fontSize: 'var(--gm-text-xs, 11.5px)',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'var(--gm-ink-muted, #596b8b)',
};

const ghostBtnSx = {
  minHeight: 44,
  px: 2,
  borderRadius: 'var(--gm-radius-control, 999px)',
  border: '1px solid var(--gm-hairline-strong, #c6d6e4)',
  color: 'var(--gm-ink, #1c3350)',
  background: 'var(--gm-surface, #fff)',
  textTransform: 'none',
  fontWeight: 600,
  boxShadow: 'none',
  transition: 'background-color 160ms ease, border-color 160ms ease',
  '&:hover': {
    background: 'var(--gm-nested, #f7fbfd)',
    borderColor: 'var(--gm-ink-muted, #596b8b)',
    boxShadow: 'none',
  },
  '&:focus-visible': {
    outline: 'none',
    boxShadow: '0 0 0 3px var(--gm-accent-tint, #d6e4fd)',
  },
};

const primaryBtnSx = {
  ...ghostBtnSx,
  border: '1px solid var(--gm-accent-ink, #3568de)',
  background: 'var(--gm-accent-ink, #3568de)',
  color: 'var(--gm-ink-on-dark, #fff)',
  '&:hover': {
    background: 'var(--gm-accent-hover, #2a5cd0)',
    borderColor: 'var(--gm-accent-hover, #2a5cd0)',
    boxShadow: 'none',
  },
};

function parseUserId(raw: string | undefined): number | null {
  const n = Number(raw);
  return raw && Number.isFinite(n) ? n : null;
}
